import React, { Component } from 'react'
import PropTypes from 'prop-types'
import URI from 'urijs'

import { getEbookContent, getAllEbookInfo, getMedia, updateUserLastPosition } from '../api/media'
import PageLayout from '../layouts/DefaultLayout'
import LoadingIndicator from '../components/media/loadingIndicator/LoadingIndicator'
import { SimpleSidebar } from '../dbWebUI'
import {
  BookContent,
  TopToolbar,
  TableOfContents,
  BottomToolbar,
  BookmarkSidebarContent,
  ReaderOptions,
} from '../components/media/bookReader'
import { parseBlr } from '../utils/media'
import { UsageTracker } from '../components/media'
import { BLR_ROUTE_PARAM, SRC_ROUTE_PARAM } from '../utils/constants'
import ErrorComponent from '../components/ErrorComponent'
import { Box } from '@deseretbook/react-ui'
import SearchSidebarContent from '../components/media/bookReader/SearchSidebarContent'
import { updateContinueMedia } from '../utils/updateContinueMedia'

import './ReaderPage.css'
import MarkupsSidebar from '../components/media/bookReader/MarkupsSidebar'

const defaultReaderOptions = {
  fontFamily: 'Georgia',
  fontSize: '1.25rem',
  highlightBrightness: 0.5,
  pageColor: { id: 'white', backgroundColor: 'white', color: 'black' },
  spacing: 1.6,
  alignment: 'left',
  layout: 1,
}

/**
 * Default page state.
 * @returns {object} default state
 */
const cleanReaderState = () => ({
  currentPage: 0,
  page: {},
  annotations: {},
  ebook: {},
  primaryContent: {},
  isLoading: true,
  displayTableOfContents: false,
  displayBookmarks: false,
  error: null,
  displayReaderOptions: false,
  notesDisplay: false,
  contentSearch: false,
  optionsDisplay: false,
  readerOptions: defaultReaderOptions,
  showToolbars: true,
  keepToolbarsOpen: false,
})

/**
 * Page for reading eBooks
 */
class ReaderPage extends Component {
  /**
   * Component constructor
   */
  constructor() {
    super()

    this.state = cleanReaderState()

    this.cache = {}

    this.getFirstVisibleBlr = () => {}

    this.updateAnnotations = () => {}

    this.annotationRef = React.createRef()

    this.onMarkupEdit = this.onMarkupEdit.bind(this)
    this.onBookmarkClick = this.onBookmarkClick.bind(this)
    this.onMarkupClick = this.onMarkupClick.bind(this)
    this.toggleNotesDisplay = this.toggleNotesDisplay.bind(this)
    this.toggleContentSearchDisplay = this.toggleContentSearchDisplay.bind(this)
    this.onBookContentSetup = this.onBookContentSetup.bind(this)
    this.toggleTableOfContentsDisplay = this.toggleTableOfContentsDisplay.bind(this)
    this.toggleBookmarksDisplay = this.toggleBookmarksDisplay.bind(this)
    this.toggleReaderOptionsDisplay = this.toggleReaderOptionsDisplay.bind(this)
    this.handleReload = this.handleReload.bind(this)
    this.handleReaderOptionsChange = this.handleReaderOptionsChange.bind(this)
    this.handleUpdateToolbars = this.handleUpdateToolbars.bind(this)
    this.updateLastPosition = this.updateLastPosition.bind(this)

    this.getPage = this.getPage.bind(this)
    this.onNextPage = this.onNextPage.bind(this)
    this.getInitialPage = this.getInitialPage.bind(this)
  }

  /**
   * Initializes data and event handlers on mount.
   */
  componentDidMount() {
    const { match } = this.props

    getMedia({ id: match.params.id })
      .then((media) => {
        // Add media to continue list
        updateContinueMedia(media, 'ebook')

        this.setState({ media })
      })
      .catch((error) => {
        this.setState({ error, isLoading: false })
      })

    // Get all the relevant book info.
    getAllEbookInfo(match.params)
      .then((ebook) => {
        this.setState({ ebook }, this.getInitialPage)
      })
      .catch((error) => {
        this.setState({
          error,
          isLoading: false,
        })
      })

    // Load the reader options from local storage.
    const readerOptionsLS = localStorage.getItem('readerOptions')
    let readerOptions = defaultReaderOptions

    if (readerOptionsLS) {
      readerOptions = JSON.parse(readerOptionsLS)
    }

    this.setState({ readerOptions })

    // Add a listener to the url history to figure out where the starting location
    // is at based on the url.
    window.addEventListener('popstate', this.getInitialPage, false)
  }

  /**
   * Cleans up event listeners before the component goes bye-bye.
   */
  componentWillUnmount() {
    // Remove all event listeners.
    window.removeEventListener('popstate', this.getInitialPage)
  }

  handleUpdateToolbars(visible, keepOpen) {
    if (this.toolbarTimeout) {
      clearTimeout(this.toolbarTimeout)
      this.toolbarTimeout = null
    }

    const newKeepOpen = this.state.keepToolbarsOpen || keepOpen

    // Prevent timeout from closing if mouse is over the toolbar.
    this.setState({
      showToolbars:
        newKeepOpen || (typeof visible === 'boolean' ? visible : !this.state.showToolbars),
      keepToolbarsOpen: keepOpen,
    })
  }

  /**
   * Determines and gets the next page based on the current one, if there isn't
   * a next page then we stay on the current page. If the `isForward` prop is
   * set to false, then the previous page is selected, but by
   *
   */
  onNextPage({ isForward = true }) {
    const { currentPage, ebook } = this.state

    const nextPosition = currentPage + (isForward ? 1 : -1)

    // If the current page order isn't found or if the requested page isn't
    // in the table of contents, then we'll just not do anything at all.
    if (nextPosition < 0 || !ebook.manifest.items.pages[nextPosition]) {
      return
    }

    const { href } = ebook.manifest.items.pages[nextPosition]

    this.getPage({ src: href })
  }

  /**
   * Event handler for when a bookmark is clicked. Uses the given information
   * to load the proper page and jump to the specified BLR.
   *
   * @param {object} blr - Parsed BLR object.
   * @param {string} documentId - this is the parameter documentId
   */
  onBookmarkClick(blr, documentId) {
    const { ebook } = this.state
    const matchingDocument = ebook.manifest.items.byDocumentId[documentId]

    this.getPage(
      {
        blr,
        src: matchingDocument.href,
      },
      () => this.toggleBookmarksDisplay({ close: true }),
    )
  }

  /**
   * Event handler for when a markup is clicked. Uses the given information
   * to load the proper page and jump to the specified BLR.
   *
   * A markup can be a highlight, note, or tag
   *
   * @param {object} blr - Parsed BLR object.
   * @param {string} documentId - this is the parameter documentId
   */
  onMarkupClick(blr, documentId, callback = () => {}) {
    const { ebook } = this.state
    const matchingDocument = ebook.manifest.items.byDocumentId[documentId]

    this.getPage(
      {
        blr,
        src: matchingDocument.href,
      },
      () => {
        this.toggleNotesDisplay({ close: true })
        callback()
      },
    )
  }

  /**
   * Opens the annotation edit modal for the specified annotation
   */
  onMarkupEdit(annotation) {
    this.onMarkupClick(annotation.blr, annotation.documentId, () => {
      // Wait a second for the content to load before opening the annotation
      setTimeout(() => {
        const api = this.annotationRef && this.annotationRef.current

        if (api && typeof api.openAnnotationById === 'function') {
          const action = annotation.note ? 'note:show' : 'edit'
          api.openAnnotationById(annotation.id, action)
        }
      }, 1000)
    })
  }

  /**
   * Callback for when the book content is first initialized. This allows us to gain
   * access to methods that the BookContent has access to.
   *
   * @param {object} params - function params
   * @param {object} params.getFirstVisibleBlr - Helper method that calculates the first visible BLR
   * @param {object} params.updateAnnotations - Method that updates the annotations displayed.
   */
  onBookContentSetup({ getFirstVisibleBlr, updateAnnotations }) {
    this.getFirstVisibleBlr = getFirstVisibleBlr
    this.updateAnnotations = updateAnnotations
  }

  updateLastPosition() {
    const { ebook } = this.state
    const { mediaId, sku } = ebook

    if (typeof this.getFirstVisibleBlr !== 'function') return

    const lastPosition = this.getFirstVisibleBlr()

    if (!lastPosition || lastPosition === 'HTML|BODY|DIV|P|0') {
      return
    }

    updateUserLastPosition({ sku, id: mediaId, lastPosition }).catch((error) => {
      console.error(error)
    })
  }

  /**
   * Determines the best document to display based on any route params, if there
   * aren't any valid route params, then it falls back to the first page found.
   * Once the right document is determined, the document is loaded.
   */
  getInitialPage() {
    const { ebook } = this.state

    // Check the current url for starting position data.
    const params = URI().search(true)

    const mediaInfoBlr = ebook && ebook.userInfo && ebook.userInfo.lastPosition
    // Check if there's a BLR in the url. If so, then load content based on that.
    const startingBlr = params[BLR_ROUTE_PARAM]
      ? parseBlr(atob(params[BLR_ROUTE_PARAM]))
      : parseBlr(mediaInfoBlr)

    if (startingBlr && startingBlr.documentId && startingBlr.documentId !== 'BODY') {
      const manifestItem = ebook.manifest.items.byDocumentId[startingBlr.documentId]

      this.getPage({ src: manifestItem.href, blr: startingBlr, addToHistory: false })

      return
    }

    // Check if there's a document source in the url. If so, then load content based on that.
    if (params[SRC_ROUTE_PARAM]) {
      this.getPage({ src: atob(params[SRC_ROUTE_PARAM]), addToHistory: false })

      return
    }

    // Fallback to loading the first page found in the table of contents.
    const firstPage = ebook.manifest.items.pages[0]

    this.getPage({ src: firstPage.href, addToHistory: false })
  }

  /**
   * Loads a page's content into the reader based on the settings provided.
   *
   * @param {object} params - getPage parameters
   * @param {string} params.src - page's relative source url.
   * @param {boolean} [params.addToHistory=true] - if the change in page should
   * be added to the route history (only applies to pages found in the TOC)
   * @param {object} [params.blr] - formatter blr object for starting position
   * @param {func} [callback= () => {}] - callback for when this method is completed.
   */
  async getPage({ src, blr, addToHistory = true }, callback = () => {}) {
    // Can't get a page without a `src`
    // `<img src="TapsTempleMeme.jpg" />`
    if (!src) {
      return
    }

    const { match } = this.props
    const { ebook } = this.state

    // Parse and clean the given src so we can properly display the content.
    const uri = new URI(src)
    const anchorId = uri.hash()
    const documentSrc = String(uri.hash(''))

    const matchingDocument = ebook.manifest.items.bySrc[documentSrc]

    const pageIndex = ebook.manifest.items.pages.findIndex((page) => page.href === documentSrc)

    const hasBlr = !!(blr && blr.selector && blr.selector.selector)

    // If there's a matching document and we want to add this to the route
    // history, then we'll add it here.
    if (matchingDocument && addToHistory) {
      const pathParams = blr
        ? { [BLR_ROUTE_PARAM]: btoa(blr.blr) }
        : { [SRC_ROUTE_PARAM]: btoa(src) }

      window.history.pushState(null, null, String(new URI().search(pathParams)))
    }

    // Build the content object we'll send down to the component that displays the content.
    const readerContent = {
      initialPosition: hasBlr ? blr.selector.selector : anchorId,
      documentId: matchingDocument ? matchingDocument.id : null,
      content: this.cache[documentSrc],
    }

    if (!readerContent.content) {
      readerContent.content = await getEbookContent({ ...match.params, href: src }).catch(
        (error) => {
          this.setState({ error })
        },
      )
      this.cache[documentSrc] = readerContent.content
    }

    // Update state with the new results.
    this.setState(
      // eslint-disable-next-line
      (prevState) => ({
        currentPage: pageIndex,
        page: matchingDocument,
        primaryContent: matchingDocument ? readerContent : prevState.primaryContent,
        isLoading: false,
      }),
      () => {
        callback(readerContent.content)
      },
    )
  }

  /**
   * Mimics page reload
   */
  handleReload() {
    this.setState(cleanReaderState(), this.componentDidMount)
  }

  /**
   * Toggles the display of the table of contents, unless `open` or `close` are specified.
   *
   * @param {object} params - function params
   * @param {boolean} [params.open=false] - Force open the table of contents
   * @param {boolean} [params.close=false] - Force close the table of contents
   */
  toggleTableOfContentsDisplay({ open = false, close = false }) {
    this.setState((prevState) => {
      // Default to toggle the current value.
      let displayTableOfContents = !prevState.displayTableOfContents

      // Force open if specified.
      if (open) {
        displayTableOfContents = true
        // Otherwise, force close if specified.
      } else if (close) {
        displayTableOfContents = false
      }

      return { displayTableOfContents }
    })
  }

  /**
   * Toggles the display of the font options
   *
   * @param {object} params - function params
   * @param {boolean} [params.open=false] - Force open the table of contents
   * @param {boolean} [params.close=false] - Force close the table of contents
   */
  toggleReaderOptionsDisplay({ open = false, close = false }) {
    this.setState((prevState) => {
      // Default to toggle the current value.
      let displayReaderOptions = !prevState.displayReaderOptions

      // Force open if specified.
      if (open) {
        displayReaderOptions = true
        // Otherwise, force close if specified.
      } else if (close) {
        displayReaderOptions = false
      }

      return { displayReaderOptions }
    })
  }
  /**
   * Toggles the display of the font options
   *
   * @param {object} params - function params
   * @param {boolean} [params.open=false] - Force open the table of contents
   * @param {boolean} [params.close=false] - Force close the table of contents
   */
  toggleNotesDisplay({ open = false, close = false }) {
    this.setState((prevState) => {
      // Default to toggle the current value.
      let notesDisplay = !prevState.notesDisplay

      // Force open if specified.
      if (open) {
        notesDisplay = true
        // Otherwise, force close if specified.
      } else if (close) {
        notesDisplay = false
      }

      return { notesDisplay }
    })
  }
  /**
   * Toggles the display of the font options
   *
   * @param {object} params - function params
   * @param {boolean} [params.open=false] - Force open the table of contents
   * @param {boolean} [params.close=false] - Force close the table of contents
   */
  toggleContentSearchDisplay({ open = false, close = false }) {
    this.setState((prevState) => {
      // Default to toggle the current value.
      let contentSearch = !prevState.contentSearch

      // Force open if specified.
      if (open) {
        contentSearch = true
        // Otherwise, force close if specified.
      } else if (close) {
        contentSearch = false
      }

      return { contentSearch }
    })
  }
  /**
   * Toggles the display of the font options
   *
   * @param {object} params - function params
   * @param {boolean} [params.open=false] - Force open the table of contents
   * @param {boolean} [params.close=false] - Force close the table of contents
   */
  toggleOptionsDisplay({ open = false, close = false }) {
    this.setState((prevState) => {
      // Default to toggle the current value.
      let optionsDisplay = !prevState.optionsDisplay

      // Force open if specified.
      if (open) {
        optionsDisplay = true
        // Otherwise, force close if specified.
      } else if (close) {
        optionsDisplay = false
      }

      return { optionsDisplay }
    })
  }

  /**
   * Toggles the display of the bookmark sidebar.
   *
   * @param {object} params - function params
   * @param {boolean} [params.open=false] - Force open the bookmark sidebar
   * @param {boolean} [params.close=false] - Force close the bookmark sidebar
   */
  toggleBookmarksDisplay({ open = false, close = false }) {
    this.setState((prevState) => {
      // Default to toggle the current value.
      let displayBookmarks = !prevState.displayBookmarks

      // Force open if specified.
      if (open) {
        displayBookmarks = true
        // Otherwise, force close if specified.
      } else if (close) {
        displayBookmarks = false
      }

      return { displayBookmarks }
    })
  }

  handleReaderOptionsChange = (key, value) => {
    this.setState((prevState) => {
      const readerOptions = {
        ...prevState.readerOptions,
        [key]: value,
      }

      // Save the reader options to local storage.
      localStorage.setItem('readerOptions', JSON.stringify(readerOptions))

      return { readerOptions }
    })
  }

  /**
   * Determines how to render the component.
   * @returns {function} Component
   */
  render() {
    const {
      annotations,
      primaryContent,
      ebook,
      isLoading,
      displayBookmarks,
      notesDisplay,
      displayTableOfContents,
      displayReaderOptions,
      readerOptions,
      contentSearch,
      optionsDisplay,
      error,
      currentPage,
      showToolbars,
      media,
    } = this.state
    const { match } = this.props

    if (isLoading) {
      return <LoadingIndicator />
    }

    const totalPages =
      ebook && ebook.manifest && ebook.manifest.items && ebook.manifest.items.pages.length

    // Calculate a percentage of how far along the user is in the book.
    const completionPercentage = ((currentPage + 1) * 100) / totalPages

    // Determine the label to display when referencing the current page.
    const pageCountLabel = `Page ${currentPage + 1} of ${totalPages}`
    const pageCountLabelWithPercentage = `${pageCountLabel} (${completionPercentage.toFixed(0)}%)`

    if (error) {
      return (
        <PageLayout>
          <ErrorComponent error={error} />
        </PageLayout>
      )
    }

    const mediaId = match.params.id

    return (
      <UsageTracker
        mediaData={{
          id: ebook.bookId,
          sku: ebook.sku,
          subscribed: ebook.userInfo.subscribed,
        }}
      >
        {({ start: startUsageTracker }) => (
          <Box id="ReaderPageContainer" style={readerOptions.pageColor}>
            <Box
              onClick={() => this.handleUpdateToolbars()}
              style={{
                height: '110vh',
                width: '150vw',
                backgroundColor: readerOptions.pageColor.backgroundColor,
                position: 'absolute',
                top: '-25px',
                left: '-50px',
                zIndex: '-1',
              }}
            />
            <TopToolbar
              readerOptions={readerOptions}
              ebook={ebook}
              showToolbars={showToolbars}
              handleUpdateToolbars={this.handleUpdateToolbars}
              onNext={this.onNextPage}
              onPrevious={() => this.onNextPage({ isForward: false })}
              onToggleBookmarks={() => this.toggleBookmarksDisplay({ open: true })}
              onMenuClick={() => this.toggleTableOfContentsDisplay({ open: true })}
              onToggleReaderOptions={() => this.toggleReaderOptionsDisplay({ open: true })}
              onToggleNotesDisplay={() => this.toggleNotesDisplay({ open: true })}
              onToggleContentSearch={() => this.toggleContentSearchDisplay({ open: true })}
              onToggleOptionsDisplay={() => this.toggleOptionsDisplay({ open: true })}
              pageCountLabel={pageCountLabel}
              sku={ebook.sku}
              mediaId={mediaId}
              media={media}
            />
            <div className="read-container" style={{ paddingTop: '5rem' }}>
              <SimpleSidebar
                hasOverlay
                isCloseable
                isOpen={displayTableOfContents}
                onClose={() => this.toggleTableOfContentsDisplay({ close: true })}
              >
                <SimpleSidebar.Content width="400px">
                  <TableOfContents
                    isVisible={displayTableOfContents}
                    tableOfContents={ebook.tableOfContents.original}
                    onSelectChapter={(ch) =>
                      this.getPage(ch, this.toggleTableOfContentsDisplay({ close: true }))
                    }
                    onClose={() => this.toggleTableOfContentsDisplay({ close: true })}
                  />
                </SimpleSidebar.Content>
              </SimpleSidebar>
              <SimpleSidebar
                hasOverlay
                isCloseable
                isOpen={displayReaderOptions}
                onClose={() => this.toggleReaderOptionsDisplay({ close: true })}
              >
                <SimpleSidebar.Content width="400px">
                  <ReaderOptions
                    isVisible={displayReaderOptions}
                    onClose={() => this.toggleReaderOptionsDisplay({ close: true })}
                    readerOptions={readerOptions}
                    handleReaderOptionsChange={this.handleReaderOptionsChange}
                  />
                </SimpleSidebar.Content>
              </SimpleSidebar>
              <BookContent
                ref={this.annotationRef}
                ebook={ebook}
                content={primaryContent}
                onNext={this.onNextPage}
                onPrevious={() => this.onNextPage({ isForward: false })}
                currentPage={currentPage + 1}
                totalPages={totalPages}
                onLinkClick={(src) => this.getPage({ src })}
                readerOptions={readerOptions}
                onLoadAnnotations={(newAnnotations) =>
                  this.setState({ annotations: newAnnotations })
                }
                onContentClick={this.handleUpdateToolbars}
                onSetup={(setupParams) => {
                  // Pass along any params to the setup callback.
                  this.onBookContentSetup(setupParams)
                  // Initialize the usage tracking and give it the context of usage.
                  startUsageTracker(setupParams.iframeDocument)
                }}
              />
              <SimpleSidebar
                hasOverlay
                isCloseable
                isOpen={displayBookmarks}
                onClose={() => this.toggleBookmarksDisplay({ close: true })}
              >
                <SimpleSidebar.Content isRight width="400px">
                  <BookmarkSidebarContent
                    bookId={ebook.bookId}
                    documentId={primaryContent.documentId}
                    annotations={annotations}
                    tableOfContents={ebook.tableOfContents}
                    manifest={ebook.manifest.items}
                    calculateBlr={this.getFirstVisibleBlr}
                    onCreate={(response) => {
                      this.setState({ annotations: response.annotations })
                      this.updateAnnotations(response)
                      this.toggleBookmarksDisplay({ close: true })
                    }}
                    onUpdate={(response) => {
                      this.setState({ annotations: response.annotations })
                      this.updateAnnotations(response)
                      this.toggleBookmarksDisplay({ close: true })
                    }}
                    onClick={this.onBookmarkClick}
                    isOpen={displayBookmarks}
                    onClose={() => this.toggleBookmarksDisplay({ close: true })}
                    defaultName={`${ebook.package.metadata.title}: Page - ${currentPage + 1}`}
                  />
                </SimpleSidebar.Content>
              </SimpleSidebar>
              <SimpleSidebar
                hasOverlay
                isCloseable
                isOpen={notesDisplay}
                onClose={() => this.toggleNotesDisplay({ close: true })}
              >
                <SimpleSidebar.Content isRight width="400px">
                  <MarkupsSidebar
                    onClose={() => this.toggleNotesDisplay({ close: true })}
                    tableOfContents={ebook.tableOfContents}
                    annotations={annotations}
                    onMarkupClick={this.onMarkupClick}
                    onMarkupEdit={this.onMarkupEdit}
                  />
                </SimpleSidebar.Content>
              </SimpleSidebar>
              <SimpleSidebar
                hasOverlay
                isCloseable
                isOpen={contentSearch}
                onClose={() => this.toggleContentSearchDisplay({ close: true })}
              >
                <SimpleSidebar.Content isRight width="400px">
                  <SearchSidebarContent
                    isOpen={displayBookmarks}
                    onClose={() => this.toggleContentSearchDisplay({ close: true })}
                    sku={ebook.sku}
                    ebook={ebook}
                    onSelectChapter={(ch) =>
                      this.getPage(ch, this.toggleContentSearchDisplay({ close: true }))
                    }
                  />
                </SimpleSidebar.Content>
              </SimpleSidebar>
              <SimpleSidebar
                hasOverlay
                isCloseable
                isOpen={optionsDisplay}
                onClose={() => this.toggleOptionsDisplay({ close: true })}
              >
                <SimpleSidebar.Content isRight>Options</SimpleSidebar.Content>
              </SimpleSidebar>
            </div>
            <BottomToolbar
              readerOptions={readerOptions}
              showToolbars={showToolbars}
              handleUpdateToolbars={this.handleUpdateToolbars}
              completionPercentage={completionPercentage}
              pageCountLabel={pageCountLabelWithPercentage}
              onNext={this.onNextPage}
              onPrevious={() => this.onNextPage({ isForward: false })}
              currentPage={currentPage + 1}
              totalPages={totalPages}
              manifest={ebook.manifest}
              toc={ebook.tableOfContents}
              onSelectChapter={(ch) => this.getPage(ch)}
            />
          </Box>
        )}
      </UsageTracker>
    )
  }
}

ReaderPage.propTypes = {
  /** Route match object received from `Route` */
  match: PropTypes.shape({}).isRequired,
}

export default ReaderPage
